import { ApiError } from '../api/client'
import { useSetAccountClassification } from '../api/hooks'
import { useAuth } from '../auth/AuthContext'
import type { Account, CostBehavior, CostTraceability } from '../api/types'

/**
 * An account's cost matrix classification.
 *
 * Two selects, not one of four values, because the axes are genuinely independent and
 * every question anyone asks is about one of them — "what is our fixed cost", "how much
 * is indirect". Collapsing them would make each of those a two-value lookup.
 *
 * Half-classified is a legal state and the control allows it: somebody working down a
 * chart of accounts knows the behaviour of an account long before they have settled its
 * traceability, and forcing both at once means neither gets recorded.
 */
export function ClassificationEditor({ account }: { account: Account }) {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('Admin')

  const save = useSetAccountClassification()

  function set(traceability: CostTraceability | null, behavior: CostBehavior | null) {
    save.mutate({ accountId: account.accountId, costTraceability: traceability, costBehavior: behavior })
  }

  const unclassified = account.costTraceability === null || account.costBehavior === null

  return (
    <div className="flex flex-col gap-1.5">
      <span className="col-head">cost classification</span>

      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input w-32"
          value={account.costTraceability ?? ''}
          disabled={!isAdmin || save.isPending}
          onChange={(e) =>
            set(
              e.target.value === '' ? null : (e.target.value as CostTraceability),
              account.costBehavior,
            )
          }
        >
          <option value="">Not set</option>
          <option value="Direct">Direct</option>
          <option value="Indirect">Indirect</option>
        </select>

        <select
          className="input w-32"
          value={account.costBehavior ?? ''}
          disabled={!isAdmin || save.isPending}
          onChange={(e) =>
            set(
              account.costTraceability,
              e.target.value === '' ? null : (e.target.value as CostBehavior),
            )
          }
        >
          <option value="">Not set</option>
          <option value="Fixed">Fixed</option>
          <option value="Variable">Variable</option>
        </select>

        {save.isPending && (
          <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
            Saving…
          </span>
        )}
      </div>

      {/*
          Said plainly, because the consequence is invisible from here: the account's whole
          spend sits outside the four cells of every cost matrix until both axes are set,
          and the matrix screen is where somebody would eventually notice the shortfall.
      */}
      {unclassified && (
        <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
          Until both are set, this account's cost is reported as unclassified rather than in
          the cost matrix.
        </span>
      )}

      <span className="text-[11px]" style={{ color: 'var(--color-faint)' }}>
        Direct cost is traceable to the department that consumes it; indirect is overhead.
        This is separate from how the cost is spread — a direct account is still allocated
        by its driver.
      </span>

      {save.isError && (
        <span className="text-[11px]" style={{ color: 'var(--color-error)' }} role="alert">
          {save.error instanceof ApiError
            ? save.error.message
            : 'The classification could not be saved.'}
        </span>
      )}
    </div>
  )
}
